import React, {
    useState,
    useEffect,
    useRef,
    useCallback,
    useLayoutEffect,
} from 'react'
import { Okpd2 } from '../../../../../Org/Handbook/types'
import { OrganizationInfo } from '../../../../../Org/Home/types'

import { ContractPerformance, Country } from '../../../../types'
import { CurrencyList } from '../../../types'

import HoverTooltip from '../../../../../../../components/HoverTooltip'

type PerfTotalDocSum = {
    total_doc: number
    total_doc_rub: number
}

type PerfTotalPaySum = {
    total_payment: number
    total_payment_rub: number
}

type Props = {
    contractGuid: string
    perf_number: number
    performance: ContractPerformance
    theme: string
    isActive: boolean
    currencyList: CurrencyList[]
    selected: boolean
    onClick: (perf_guid: string) => void
    onRightClick: (perf_guid: string) => void
    onDoubleClick: (perf_guid: string) => void
}

const PERFORMANCE_STATUS = new Map([
    ['F', 'Редактирование'],
    ['P', 'Размещено'],
    ['I', 'Недействительно'],
    ['M', 'Изменение'],
])

const getCurrentDate = (): string => {
    const today = new Date()
    const year = today.getFullYear()
    const month = String(today.getMonth() + 1).padStart(2, '0') // добавляем 1, так как месяцы начинаются с 0
    const day = String(today.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

function formatCountryOfOriginStrict(src: Country | null | undefined): string {
    const list = src?.country ?? []
    if (!Array.isArray(list) || list.length === 0) return ''

    const names = list.map((c) => (c?.name ?? '').trim()).filter(Boolean)

    // убрать дубли, сохраняя порядок
    const seen = new Set<string>()
    const norm = (s: string) => s.toLocaleLowerCase('ru-RU')
    const unique = names.filter((n) => {
        const k = norm(n)
        if (seen.has(k)) return false
        seen.add(k)
        return true
    })

    // РФ — первой
    const rfIndex = unique.findIndex((n) => norm(n) === 'российская федерация')
    if (rfIndex > 0) {
        const [rf] = unique.splice(rfIndex, 1)
        unique.unshift(rf)
    }

    return unique.join(', ')
}

function getCountryListOrderedStrict(
    src: Country | null | undefined
): string[] {
    const list = src?.country ?? []
    if (!Array.isArray(list) || list.length === 0) return []

    const names = list.map((c) => (c?.name ?? '').trim()).filter(Boolean)

    // dedupe (без регистра), сохранив порядок
    const seen = new Set<string>()
    const norm = (s: string) => s.toLocaleLowerCase('ru-RU')
    const unique = names.filter((n) => {
        const k = norm(n)
        if (seen.has(k)) return false
        seen.add(k)
        return true
    })

    // РФ — первой
    const rfIndex = unique.findIndex((n) => norm(n) === 'российская федерация')
    if (rfIndex > 0) {
        const [rf] = unique.splice(rfIndex, 1)
        unique.unshift(rf)
    }

    return unique
}

function purchaseTypeLabel(code?: string | null): string {
    switch ((code ?? '').toUpperCase()) {
        case 'G':
            return 'Товар'
        case 'W':
            return 'Работа'
        case 'S':
            return 'Услуга'
        default:
            return '' // неизвестно/пусто
    }
}

function formatTotalCount(totalCount: number) {
    // Получаем число с 5 знаками после запятой (с округлением)
    let str = totalCount.toFixed(5)
    // Если есть десятичная часть, удаляем лишние нули
    if (str.indexOf('.') !== -1) {
        // Удаляем концевые нули
        str = str.replace(/0+$/, '')
        // Если после удаления нулей осталась точка, убираем её
        str = str.replace(/\.$/, '')
    }
    // Заменяем точку на запятую
    return str.replace('.', ',')
}

const formatNumber = (num: number | string | null | undefined): string => {
    if (num === null || num === undefined) return '-' // Обрабатываем пустые значения

    const parsedNum = typeof num === 'number' ? num : parseFloat(num)

    if (isNaN(parsedNum)) {
        return '-' // Обрабатываем некорректные значения
    }

    return parsedNum
        .toFixed(2) // Два знака после запятой
        .replace(/\B(?=(\d{3})+(?!\d))/g, ' ') // Разделяем тысячные пробелами
        .replace('.', ',') // Заменяем точку на запятую
}

const ContractPerformanceTableRow: React.FC<Props> = (props) => {
    const [performanceTotalDocSum, setPerformanceTotalDocSum] =
        useState<PerfTotalDocSum | null>()
    const [performanceTotalPaySum, setPerformanceTotalPaySum] =
        useState<PerfTotalPaySum | null>()
    const [isLoadingPerformanceTotalSum, setLoadingPerformanceTotalSum] =
        useState(false)

    const STATUS_COLOR =
        props.theme == 'dark'
            ? new Map([
                  ['Черновик', '#8D79FF'],
                  ['Готово', '#049664'],
                  ['Подготовка', '#745CFF'],
                  ['Готово к отправке', '#563AF5'],
                  ['Отправлено в ЕИС', '#049664'],
                  ['Размещена', '#636366'],
                  ['Недействительно', '#636366'],
              ])
            : new Map([
                  ['Черновик', '#DFDDFF'],
                  ['Готово', '#83D2B7'],
                  ['Подготовка', '#D3CFFF'],
                  ['Готово к отправке', '#B7ACFF'],
                  ['Отправлено в ЕИС', '#83D2B7'],
                  ['Размещена', '#CFD6E2'],
                  ['Недействительно', '#ED9CAE'],
              ])

    const STAGE_COLOR =
        props.theme == 'dark'
            ? new Map([
                  ['Редактирование', '#745CFF'],
                  ['Подготовка к размещению', '#563AF5'],
                  ['Отправка в ЕИС', '#4406BE'],
                  ['Размещенное исполнение', '#636366'],
              ])
            : new Map([
                  ['Редактирование', '#D3CFFF'],
                  ['Подготовка к размещению', '#B7ACFF'],
                  ['Отправка в ЕИС', '#A191FF'],
                  ['Размещенное исполнение', '#CFD6E2'],
              ])

    const PERFORMANCE_STATUS_COLOR =
        props.theme == 'dark'
            ? new Map([
                  ['F', '#8E8E93'],
                  ['P', '#2A282B00'],
                  ['I', '#3A3A3C'],
                  ['M', '#636366'],
              ])
            : new Map([
                  ['F', '#E4E8F0'],
                  ['P', '#F6FBFF00'],
                  ['I', '#B7BECA'],
                  ['M', '#CFD6E2'],
              ])

    const updatedDate = new Date(props.performance.publication_date)
    updatedDate.setHours(updatedDate.getHours())

    const formattedUpdateDate = updatedDate
        .toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        })
        .replace(/\//g, '.')
    const formattedUpdateTime = updatedDate.toLocaleTimeString('ru-RU', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    })

    const loadPerformanceTotalDocSum = async () => {
        setLoadingPerformanceTotalSum(true)

        const url = new URL(
            `${process.env.REACT_APP_API_URL}/api/backend/v1/performance_contract_doc/performance_contract_guid/total_doc/${props.performance?.guid}`
        )
        try {
            const response = await fetch(url.toString(), {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
            })
            if (!response.ok) {
                setPerformanceTotalDocSum(null)
                throw new Error(
                    `Error: ${response.status} ${response.statusText}`
                )
            }
            const json: PerfTotalDocSum = await response.json()
            setPerformanceTotalDocSum(json)
        } catch (e) {
            console.error('Error fetching count/sum:', e)
            setPerformanceTotalDocSum(null)
        } finally {
            setLoadingPerformanceTotalSum(false)
        }
    }

    const loadPerformanceTotalPaySum = async () => {
        setLoadingPerformanceTotalSum(true)

        const url = new URL(
            `${process.env.REACT_APP_API_URL}/api/backend/v1/performance_contract_payment/performance_contract_guid/total_payment/${props.performance?.guid}`
        )
        try {
            const response = await fetch(url.toString(), {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
            })
            if (!response.ok) {
                setPerformanceTotalDocSum(null)
                throw new Error(
                    `Error: ${response.status} ${response.statusText}`
                )
            }
            const json: PerfTotalPaySum = await response.json()
            setPerformanceTotalPaySum(json)
        } catch (e) {
            console.error('Error fetching count/sum:', e)
            setPerformanceTotalPaySum(null)
        } finally {
            setLoadingPerformanceTotalSum(false)
        }
    }

    useEffect(() => {
        const fetchData = async () => {
            try {
                const total_doc = await loadPerformanceTotalDocSum()
                if (total_doc != null) {
                    setPerformanceTotalDocSum(total_doc)
                }
                const total_pay = await loadPerformanceTotalPaySum()
                if (total_pay != null) {
                    setPerformanceTotalPaySum(total_pay)
                }
            } catch (error) {
                console.error('Ошибка при загрузке данных:', error)
            }
        }
        fetchData()
    }, [])

    return (
        <div
            className={
                props.selected
                    ? 'plan-position-table-row-selected'
                    : 'plan-position-table-row'
            }
            onClick={() => {
                props.performance.performance_stage == 'Размещенное исполнение'
                    ? ''
                    : props.onClick(props.performance.guid)
            }}
            onContextMenu={(e) => {
                e.preventDefault() // Отключает стандартное контекстное меню
                props.performance.performance_stage == 'Размещенное исполнение'
                    ? ''
                    : props.onRightClick(props.performance.guid)
            }}
            onDoubleClick={() => props.onDoubleClick(props.performance.guid)}
        >
            <div className="flex h-[70px] w-8 items-center justify-center text-center">
                {props.performance.performance_stage ==
                'Размещенное исполнение' ? (
                    ''
                ) : (
                    <input
                        type="checkbox"
                        checked={props.selected}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => {
                            props.onClick(props.performance.guid)
                        }}
                    />
                )}
            </div>
            <div className="flex flex-row h-[70px] w-8 items-center justify-center text-center ">
                {props.perf_number}
            </div>

            <div
                className={`flex h-[70px] w-24 p-1 items-center justify-center text-center rounded`}
                style={{
                    backgroundColor: props.performance.performance_stage
                        ? STAGE_COLOR.get(props.performance.performance_stage)
                        : 'transparent',
                }}
            >
                {props.performance.performance_stage}
            </div>
            <div
                className="flex h-[70px] w-24 p-1 items-center justify-center text-center rounded"
                style={{
                    backgroundColor: props.performance.performance_status
                        ? STATUS_COLOR.get(props.performance.performance_status)
                        : 'transparent',
                }}
            >
                {props.performance.performance_status == 'Размещена'
                    ? 'Размещено'
                    : props.performance.performance_status}
            </div>

            <div className="flex flex-1 h-[70px] text-left items-center justify-center">
                {`Сведения об исполнении по договору № ${props.performance.registration_number ? props.performance.registration_number : '-'}`}
            </div>

            <div
                className="flex h-[70px] w-24 items-center justify-center text-center rounded"
                style={{
                    backgroundColor: props.performance.status
                        ? PERFORMANCE_STATUS_COLOR.get(props.performance.status)
                        : 'transparent',
                }}
            >
                {PERFORMANCE_STATUS.get(String(props.performance.status))}
            </div>

            <div className="flex flex-row h-[70px] w-28 text-right items-center justify-end">
                {isLoadingPerformanceTotalSum ? (
                    <div className="flex w-8 items-center justify-center text-center text-[#8A8A8A] ">
                        <svg
                            className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                            ></circle>
                            <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                            ></path>
                        </svg>
                    </div>
                ) : performanceTotalDocSum?.total_doc_rub ? (
                    Number(
                        performanceTotalDocSum?.total_doc_rub
                    ).toLocaleString('ru-RU', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 13,
                    })
                ) : (
                    performanceTotalDocSum?.total_doc_rub
                )}
            </div>

            <div className="flex flex-row h-[70px] w-28 text-right items-center justify-end">
                {isLoadingPerformanceTotalSum ? (
                    <div className="flex w-8 items-center justify-center text-center text-[#8A8A8A] ">
                        <svg
                            className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                            ></circle>
                            <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                            ></path>
                        </svg>
                    </div>
                ) : performanceTotalPaySum?.total_payment_rub ? (
                    Number(
                        performanceTotalPaySum?.total_payment_rub
                    ).toLocaleString('ru-RU', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 13,
                    })
                ) : (
                    performanceTotalPaySum?.total_payment_rub
                )}
            </div>

            <div className="flex flex-row h-[70px] w-20 items-center justify-center text-center">
                {props.performance.version}
            </div>

            <div className="flex h-[70px] w-20 items-center justify-center text-center text-[#6A6A6A] dark:text-[#8A8A8A]">
                {props.performance.publication_date ? (
                    <>
                        {formattedUpdateDate} <br /> {formattedUpdateTime}{' '}
                    </>
                ) : (
                    '-'
                )}
            </div>
        </div>
    )
}

export default ContractPerformanceTableRow
